import { useMutation } from '@apollo/client';
import { UploadImageToWorkspaceDocument, UploadImageToTemplateDocument } from '~/api/graphql';

export interface ImageUploadOptions {
  workspaceId?: string;
  templateId?: string;
  artifactId: string;
}

export const useImageUpload = (options: ImageUploadOptions) => {
  const [uploadToWorkspaceMutation, { loading: workspaceLoading, error: workspaceError }] =
    useMutation(UploadImageToWorkspaceDocument);
  const [uploadToTemplateMutation, { loading: templateLoading, error: templateError }] =
    useMutation(UploadImageToTemplateDocument);

  const uploadImage = async (file: File): Promise<{ url: string; fileId: string }> => {
    try {
      // Determine context and use appropriate mutation
      if (options.templateId) {
        // Template context: upload with HTML_DOCUMENT_TEMPLATE entity reference
        const result = await uploadToTemplateMutation({
          variables: {
            templateId: options.templateId,
            file: {
              file,
              type: 'OTHER',
              visible: true,
              notes: `Image uploaded to artifact ${options.artifactId}`
            }
          }
        });

        if (!result.data?.uploadHtmlDocumentTemplateFile) {
          throw new Error('Failed to upload image to template');
        }

        const uploadedFile = result.data.uploadHtmlDocumentTemplateFile;
        return {
          url: uploadedFile.url || '',
          fileId: uploadedFile.id
        };
      } else if (options.workspaceId) {
        // Workspace context: upload with CARA_WORKSPACE entity reference
        const result = await uploadToWorkspaceMutation({
          variables: {
            workspaceId: options.workspaceId,
            file: {
              file,
              type: 'OTHER',
              visible: true,
              notes: `Image uploaded to artifact ${options.artifactId}`
            }
          }
        });

        if (!result.data?.uploadWorkspaceFile) {
          throw new Error('Failed to upload image to workspace');
        }

        const uploadedFile = result.data.uploadWorkspaceFile;
        return {
          url: uploadedFile.url || '',
          fileId: uploadedFile.id
        };
      } else {
        throw new Error('Either workspaceId or templateId must be provided');
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Image upload failed:', err);
      throw err;
    }
  };

  return {
    uploadImage,
    loading: workspaceLoading || templateLoading,
    error: workspaceError || templateError
  };
};
